import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Upload, LogOut, Loader2, FileArchive, ExternalLink, KeyRound, Eye, EyeOff, Copy, Tag, Trash2, Plus, Power } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LIFECYCLE_STYLES = {
  DRAFT: "bg-slate-100 text-slate-600",
  READY: "bg-blue-100 text-blue-700",
  LIVE: "bg-green-100 text-green-700",
  UNPUBLISHED: "bg-amber-100 text-amber-700",
  ARCHIVED: "bg-slate-200 text-slate-500",
};
const CH_STYLES = {
  published: "bg-green-100 text-green-700", pending: "bg-slate-100 text-slate-500",
  manual_action_required: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700",
  unpublished: "bg-slate-200 text-slate-500", not_applicable: "bg-slate-50 text-slate-400",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [oauth, setOauth] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/mcp-oauth").then((r) => setOauth(r.data)).catch(() => {}); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/ingest", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.success && data.validation.valid) toast.success(`${data.created ? "Draft created" : "Draft updated"}: ${data.product_key}`);
      else toast.error("Validation failed — see report");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0A0F1D] text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-[#3B1F6E] flex items-center justify-center"><Crown className="w-4 h-4 text-[#D97706]" /></span>
            <span className="font-serif-display text-xl font-semibold">Quick Publisher</span>
            <span className="text-xs text-slate-400 ml-2">Console</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="text-sm text-slate-300 hover:text-white flex items-center gap-1">Storefront <ExternalLink className="w-3.5 h-3.5" /></Link>
            <span className="text-sm text-slate-400">{user?.email}</span>
            <button data-testid="logout-button" onClick={logout} className="p-2 hover:bg-white/10 rounded-lg"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-[#0A0F1D] mb-1 flex items-center gap-2"><Upload className="w-4 h-4 text-[#3B1F6E]" /> Upload Product ZIP</h2>
            <p className="text-sm text-slate-500 mb-4">Drop a QRU Product ZIP. Manual upload and ChatGPT (MCP) use the same ingestion pipeline.</p>
            <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              data-testid="zip-dropzone"
              className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-[#3B1F6E] hover:bg-[#3B1F6E]/5 transition-colors">
              {uploading ? <Loader2 className="w-8 h-8 text-[#3B1F6E] animate-spin mx-auto" /> : <FileArchive className="w-8 h-8 text-slate-400 mx-auto" />}
              <p className="text-sm text-slate-500 mt-3">{uploading ? "Ingesting..." : "Click or drag a .zip file here"}</p>
              <input ref={fileRef} data-testid="zip-input" type="file" accept=".zip" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-[#0A0F1D] mb-3">Validation Report</h2>
            {!result ? <p className="text-sm text-slate-400">Upload a package to see results.</p> : (
              <div data-testid="validation-report" className="text-sm space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">Product</span><span className="font-mono text-xs">{result.product_key}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Action</span><span>{result.created ? "Created" : result.updated ? "Updated" : "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Version</span><span>v{result.package_version}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Valid</span><span className={result.validation?.valid ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>{result.validation?.valid ? "Yes" : "No"}</span></div>
                {result.validation?.errors?.length > 0 && (
                  <ul className="mt-2 text-xs text-red-600 list-disc pl-4 space-y-1">{result.validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                )}
              </div>
            )}
          </div>
        </div>

        {oauth && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8" data-testid="mcp-oauth-card">
            <h2 className="font-semibold text-[#0A0F1D] mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#3B1F6E]" /> ChatGPT MCP — OAuth Client</h2>
            <p className="text-sm text-slate-500 mb-4">Enter these in ChatGPT Business → Advanced OAuth → User-Defined OAuth Client. The Client Secret is visible only here, behind your login.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <OAuthRow label="Client ID" value={oauth.client_id} testid="oauth-client-id" />
              <OAuthRow label="Scopes" value={oauth.scopes} testid="oauth-scopes" />
              <OAuthRow label="Authorization URL" value={oauth.authorization_endpoint} testid="oauth-auth-url" />
              <OAuthRow label="Token URL" value={oauth.token_endpoint} testid="oauth-token-url" />
              <OAuthRow label="MCP Server URL" value={oauth.mcp_url} testid="oauth-mcp-url" />
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Client Secret</label>
                <div className="flex items-center gap-2">
                  <code data-testid="oauth-client-secret" className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-mono text-xs break-all min-w-0">{showSecret ? oauth.client_secret : "•".repeat(28)}</code>
                  <button data-testid="reveal-secret" onClick={() => setShowSecret((s) => !s)} className="p-2 rounded-lg hover:bg-slate-100 shrink-0">{showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  <button data-testid="copy-secret" onClick={() => { navigator.clipboard.writeText(oauth.client_secret); toast.success("Client Secret copied"); }} className="p-2 rounded-lg hover:bg-slate-100 shrink-0"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        )}

        <PromoCodes />

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-semibold text-[#0A0F1D]">Product Catalog ({products.length})</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Product</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Lifecycle</th>
                  <th className="px-4 py-3">QRU</th><th className="px-4 py-3">Shopify</th><th className="px-4 py-3">Etsy</th><th className="px-4 py-3">YouTube</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.length === 0 ? (
                  <tr><td colSpan={8} className="px-6 py-12 text-center text-slate-400">No products yet. Upload a ZIP to begin.</td></tr>
                ) : products.map((p) => (
                  <tr key={p.product_key} data-testid={`catalog-row-${p.product_key}`} className="hover:bg-slate-50">
                    <td className="px-6 py-3"><div className="font-medium text-[#0A0F1D]">{p.title}</div><div className="text-xs text-slate-400 font-mono">{p.product_key}</div></td>
                    <td className="px-4 py-3">${p.price.toFixed(2)}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${LIFECYCLE_STYLES[p.lifecycle_status]}`}>{p.lifecycle_status}</span></td>
                    {["qru_online", "shopify", "etsy", "youtube"].map((c) => (
                      <td key={c} className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${CH_STYLES[p.publishing_summary?.[c]] || CH_STYLES.pending}`}>{(p.publishing_summary?.[c] || "pending").replace(/_/g, " ")}</span></td>
                    ))}
                    <td className="px-4 py-3 text-right"><Link data-testid={`manage-${p.product_key}`} to={`/admin/product/${p.product_key}`} className="text-[#3B1F6E] font-semibold hover:underline">Manage</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function PromoCodes() {
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/discounts").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!code.trim() || !value) { toast.error("Enter a code and value"); return; }
    setSaving(true);
    try {
      await api.post("/admin/discounts", { code: code.trim(), type, value: parseFloat(value) });
      toast.success("Promo code created");
      setCode(""); setValue(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create code");
    } finally { setSaving(false); }
  };

  const toggle = async (c) => { try { await api.patch(`/admin/discounts/${c.code}`, { active: !c.active }); load(); } catch { toast.error("Update failed"); } };
  const del = async (c) => { try { await api.delete(`/admin/discounts/${c.code}`); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8" data-testid="promo-codes-card">
      <h2 className="font-semibold text-[#0A0F1D] mb-1 flex items-center gap-2"><Tag className="w-4 h-4 text-[#3B1F6E]" /> Promo Codes</h2>
      <p className="text-sm text-slate-500 mb-4">Global discount codes — they apply to any cart, including all current and future products.</p>
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Code</label>
          <input data-testid="promo-new-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="LAUNCH20"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-[#3B1F6E]/30" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Type</label>
          <select data-testid="promo-new-type" value={type} onChange={(e) => setType(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="percent">% off</option>
            <option value="fixed">$ off</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Value</label>
          <input data-testid="promo-new-value" type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "percent" ? "20" : "5"}
            className="h-10 w-28 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B1F6E]/30" />
        </div>
        <button data-testid="promo-create" onClick={create} disabled={saving}
          className="h-10 px-4 rounded-lg bg-[#3B1F6E] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
        </button>
      </div>
      {codes.length === 0 ? (
        <p className="text-sm text-slate-400">No promo codes yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Discount</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Redeemed</th><th className="px-4 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {codes.map((c) => (
                <tr key={c.code} data-testid={`promo-row-${c.code}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono font-semibold text-[#2E1065]">{c.code}</td>
                  <td className="px-4 py-2">{c.type === "percent" ? `${c.value}% off` : `$${Number(c.value).toFixed(2)} off`}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                  <td className="px-4 py-2 text-slate-500">{c.redemption_count || 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button data-testid={`promo-toggle-${c.code}`} onClick={() => toggle(c)} title={c.active ? "Deactivate" : "Activate"} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Power className="w-4 h-4" /></button>
                    <button data-testid={`promo-delete-${c.code}`} onClick={() => del(c)} title="Delete" className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 ml-1"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OAuthRow({ label, value, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <code data-testid={testid} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-mono text-xs break-all min-w-0">{value}</code>
        <button onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); }} className="p-2 rounded-lg hover:bg-slate-100 shrink-0"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

